import { __ } from "@wordpress/i18n";
import {
	PanelBody,
	RangeControl,
	SelectControl,
	ButtonGroup,
	Button,
	__experimentalUnitControl as UnitControl,
} from "@wordpress/components";
import { InspectorControls } from "@wordpress/block-editor";
import { useSelect } from "@wordpress/data";
import { useState } from "@wordpress/element";

const DEVICES = [
	{ label: __("Desktop", "sfb"), value: "Desktop" },
	{ label: __("Tablet", "sfb"), value: "Tablet" },
	{ label: __("Mobile", "sfb"), value: "Mobile" },
];

const ALIGN_SELF_OPTIONS = [
	{ label: __("Auto (Inherit)", "sfb"), value: "auto" },
	{ label: __("Flex Start", "sfb"), value: "flex-start" },
	{ label: __("Center", "sfb"), value: "center" },
	{ label: __("Flex End", "sfb"), value: "flex-end" },
	{ label: __("Stretch", "sfb"), value: "stretch" },
	{ label: __("Baseline", "sfb"), value: "baseline" },
];

const FlexItemControls = ({ attributes, setAttributes, clientId }) => {
	const [activeDevice, setActiveDevice] = useState("Desktop");

	// Check if this block's parent is a flex container
	const isInFlexContainer = useSelect(
		(select) => {
			const { getBlockParents, getBlock } = select("core/block-editor");
			const parentIds = getBlockParents(clientId);

			if (parentIds.length === 0) return false;

			const immediateParent = getBlock(parentIds[parentIds.length - 1]);
			return (
				immediateParent?.name === "sfb/container" &&
				immediateParent?.attributes?.displayType === "flex"
			);
		},
		[clientId],
	);

	// Don't show controls if not in flex container
	if (!isInFlexContainer) return null;

	const getAttributeName = (property) => {
		return `${property}${activeDevice}`;
	};

	const getValue = (property, defaultValue) => {
		return attributes[getAttributeName(property)] ?? defaultValue;
	};

	const setValue = (property, value) => {
		setAttributes({
			[getAttributeName(property)]: value,
		});
	};

	return (
		<InspectorControls>
			<PanelBody title={__("Flex Item", "sfb")} initialOpen={false}>
				{/* Device Tabs */}
				<div style={{ marginBottom: "16px" }}>
					<ButtonGroup>
						{DEVICES.map((device) => (
							<Button
								key={device.value}
								variant={
									activeDevice === device.value ? "primary" : "secondary"
								}
								onClick={() => setActiveDevice(device.value)}
							>
								{device.label}
							</Button>
						))}
					</ButtonGroup>
				</div>

				<RangeControl
					label={__("Flex Grow", "sfb")}
					help={__("How much this item should grow relative to others", "sfb")}
					value={getValue("flexGrow", 1)}
					onChange={(value) => setValue("flexGrow", value)}
					min={0}
					max={10}
					step={1}
				/>

				<RangeControl
					label={__("Flex Shrink", "sfb")}
					help={__(
						"How much this item should shrink relative to others",
						"sfb",
					)}
					value={getValue("flexShrink", 1)}
					onChange={(value) => setValue("flexShrink", value)}
					min={0}
					max={10}
					step={1}
				/>

				<UnitControl
					label={__("Flex Basis", "sfb")}
					help={__("Initial size of the item before growing/shrinking", "sfb")}
					value={getValue("flexBasis", "auto")}
					onChange={(value) => setValue("flexBasis", value || "auto")}
					units={[
						{ value: "auto", label: "auto" },
						{ value: "px", label: "px" },
						{ value: "%", label: "%" },
						{ value: "rem", label: "rem" },
					]}
				/>

				<SelectControl
					label={__("Align Self", "sfb")}
					help={__("Override parent's align-items for this item", "sfb")}
					value={getValue("alignSelf", "auto")}
					options={ALIGN_SELF_OPTIONS}
					onChange={(value) => setValue("alignSelf", value)}
				/>

				<RangeControl
					label={__("Order", "sfb")}
					help={__("Change visual order of this item", "sfb")}
					value={getValue("order", 0)}
					onChange={(value) => setValue("order", value)}
					min={-10}
					max={10}
					step={1}
				/>
			</PanelBody>
		</InspectorControls>
	);
};

export default FlexItemControls;
